import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { ServerConfig, fullHttpURL } from "../serverConfig";
import { palette } from "../theme";
import { PackageOpen } from "lucide-react";

export interface LiveOrder {
  id: string; authorName: string; profileUrl: string; commentIds: string[]; sourceMessages: string[];
  recipientName: string; phone: string; address: string; productCode: string; productName: string;
  color: string; size: string; quantity: number; unitPrice: number; shippingFee: number;
  paymentMethod: string; customerConfirmed: boolean; status: string; printStatus: string;
}

async function errorText(response: Response): Promise<string> {
  try { return (await response.json() as { error?: string }).error || `HTTP ${response.status}`; }
  catch { return `HTTP ${response.status}`; }
}

export function LiveOrders({ serverConfig, onCount }: { serverConfig: ServerConfig; onCount?: (count: number) => void }): JSX.Element {
  const [orders, setOrders] = useState<LiveOrder[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = fullHttpURL(serverConfig) + "/orders/live";
  const reload = useCallback(async () => {
    try {
      const response = await fetch(base);
      if (!response.ok) throw new Error(await errorText(response));
      const value = await response.json() as LiveOrder[];
      setOrders(value);
      setSelected(old => old && value.some(order => order.id === old) ? old : value[0]?.id ?? null);
      setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : "Không tải được đơn"); }
  }, [base]);
  useEffect(() => {
    void reload();
    const refresh = () => void reload();
    const timer = window.setInterval(refresh, 2000);
    window.addEventListener("live-orders-changed", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("live-orders-changed", refresh); };
  }, [reload]);
  const order = orders.find(item => item.id === selected);
  useEffect(() => { onCount?.(orders.length); }, [orders.length, onCount]);
  const patch = (field: keyof LiveOrder, value: string | number | boolean) => {
    if (!order) return;
    setOrders(current => current.map(item => item.id === order.id ? { ...item, [field]: value } : item));
  };
  const save = async () => {
    if (!order) return;
    const response = await fetch(`${base}/${order.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(order) });
    if (!response.ok) { setError(await errorText(response)); return; }
    await reload();
  };
  const approve = async () => {
    if (!order) return;
    await save();
    const response = await fetch(`${base}/${order.id}/approve`, { method: "POST" });
    if (!response.ok) { setError(await errorText(response)); return; }
    await reload();
  };
  const print = () => {
    if (!order || order.status !== "APPROVED") return;
    window.print();
  };
  const markPrinted = async () => {
    if (!order || order.status !== "APPROVED") return;
    const response = await fetch(`${base}/${order.id}/printed`, { method: "POST" });
    if (!response.ok) { setError(await errorText(response)); return; }
    await reload();
  };
  const total = order ? order.quantity * order.unitPrice + order.shippingFee : 0;
  return <Box id="live-orders" sx={{ p: 2, border: `1px solid ${palette.border}`, borderRadius: 2, bgcolor: palette.surface }}>
    <Typography variant="h2" sx={{ mb: 1 }}>Đơn Live ({orders.length})</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Chọn “Tạo đơn nháp” tại comment mua hàng. Nhiều comment của cùng tài khoản sẽ được gom vào một đơn chưa duyệt.</Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {!order ? <Box sx={{ border: `1px solid ${palette.border}`, borderRadius: 2, py: 5, px: 2, textAlign: "center", bgcolor: palette.cream }}><PackageOpen size={48} color={palette.borderStrong}/><Typography sx={{ mt: 1, fontWeight: 600 }}>Chưa có đơn hàng</Typography><Typography variant="body2">Khi tạo đơn nháp từ bình luận mua hàng, đơn sẽ hiển thị tại đây.</Typography></Box> : <>
      <TextField select fullWidth label="Đơn đang xử lý" value={selected ?? ""} onChange={e => setSelected(e.target.value)} sx={{ mb: 2 }}>
        {orders.map(item => <MenuItem key={item.id} value={item.id}>{item.authorName} · {item.status} · {item.commentIds.length} comment</MenuItem>)}
      </TextField>
      <Alert severity={order.status === "APPROVED" ? "success" : "warning"} sx={{ mb: 2 }}>
        {order.status === "APPROVED" ? `Đã duyệt · ${order.printStatus}` : "Đơn chờ thông tin và duyệt"}
      </Alert>
      <Box className="no-print" sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
        <TextField label="Mã sản phẩm" value={order.productCode} disabled={order.status === "APPROVED"} onChange={e => patch("productCode", e.target.value)} />
        <TextField label="Tên sản phẩm" value={order.productName} disabled={order.status === "APPROVED"} onChange={e => patch("productName", e.target.value)} />
        <TextField label="Màu" value={order.color} disabled={order.status === "APPROVED"} onChange={e => patch("color", e.target.value)} />
        <TextField label="Size" value={order.size} disabled={order.status === "APPROVED"} onChange={e => patch("size", e.target.value)} />
        <TextField label="Số lượng" type="number" value={order.quantity} disabled={order.status === "APPROVED"} onChange={e => patch("quantity", Number(e.target.value))} />
        <TextField label="Đơn giá" type="number" value={order.unitPrice} disabled={order.status === "APPROVED"} onChange={e => patch("unitPrice", Number(e.target.value))} />
        <TextField label="Tên người nhận" value={order.recipientName} disabled={order.status === "APPROVED"} onChange={e => patch("recipientName", e.target.value)} />
        <TextField label="SĐT" value={order.phone} disabled={order.status === "APPROVED"} onChange={e => patch("phone", e.target.value)} />
        <TextField label="Phí giao" type="number" value={order.shippingFee} disabled={order.status === "APPROVED"} onChange={e => patch("shippingFee", Number(e.target.value))} />
        <TextField label="Địa chỉ" value={order.address} disabled={order.status === "APPROVED"} onChange={e => patch("address", e.target.value)} />
        <TextField select label="Thanh toán" value={order.paymentMethod} disabled={order.status === "APPROVED"} onChange={e => patch("paymentMethod", e.target.value)}>
          <MenuItem value="COD">Thanh toán khi nhận hàng</MenuItem><MenuItem value="BANK_TRANSFER">Chuyển khoản</MenuItem><MenuItem value="PICKUP">Nhận tại cửa hàng</MenuItem>
        </TextField>
      </Box>
      <Typography sx={{ mt: 2 }}><strong>Comment gốc:</strong> {order.sourceMessages.join(" · ")}</Typography>
      <Typography sx={{ mt: 1 }}><strong>Tổng:</strong> {total.toLocaleString("vi-VN")} đ</Typography>
      <FormControlLabel control={<Checkbox checked={order.customerConfirmed} disabled={order.status === "APPROVED"} onChange={e => patch("customerConfirmed", e.target.checked)} />} label="Khách đã xác nhận hàng, số lượng, tổng tiền và địa chỉ" />
      <Box id="print-order" className="print-only">
        <Typography variant="h2">PHIẾU ĐÓNG GÓI</Typography>
        <Typography><strong>Mã đơn:</strong> {order.id}</Typography>
        <Typography><strong>Người nhận:</strong> {order.recipientName} · {order.phone}</Typography>
        <Typography><strong>Địa chỉ:</strong> {order.address}</Typography>
        <Typography><strong>Sản phẩm:</strong> {order.productCode} · {order.productName}, {order.color}, size {order.size}, SL {order.quantity}</Typography>
        <Typography><strong>Thanh toán:</strong> {order.paymentMethod} · {total.toLocaleString("vi-VN")} đ</Typography>
      </Box>
      <Box className="no-print" sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
        {order.status !== "APPROVED" && <><Button variant="outlined" onClick={() => void save()}>Lưu đơn chờ</Button><Button variant="contained" onClick={() => void approve()}>Duyệt đơn</Button></>}
        <Button variant="contained" color="success" disabled={order.status !== "APPROVED"} onClick={print}>In đơn đã duyệt</Button>
        <Button variant="outlined" color="success" disabled={order.status !== "APPROVED" || order.printStatus === "PRINTED"} onClick={() => void markPrinted()}>Đánh dấu đã in</Button>
      </Box>
    </>}
  </Box>;
}
